/**
 * Componente de navegação por abas do Flow Builder
 * Design premium com abas de largura fixa: Fluxo, Temas, Compartilhar, Analytics
 * Todas as abas sempre mostram ícone + label para evitar deslocamento visual
 */

import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { 
  Workflow, 
  Palette, 
  Share2, 
  Activity 
} from 'lucide-react';

interface TabItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
}

/**
 * Abas de navegação premium do Flow Builder
 * Largura fixa em todas as abas para estabilidade visual
 */
export function FlowBuilderTabs() {
  const navigate = useNavigate();
  const location = useLocation();
  const { flowId } = useParams();

  // Definição das abas em português
  const tabs: TabItem[] = [
    {
      id: 'flow',
      label: 'Fluxo',
      icon: Workflow,
      path: `/tenant/flows/${flowId}`,
    },
    {
      id: 'theme',
      label: 'Temas',
      icon: Palette,
      path: `/tenant/flows/${flowId}/theme`,
    },
    {
      id: 'share',
      label: 'Compartilhar',
      icon: Share2,
      path: `/tenant/flows/${flowId}/share`,
    },
    {
      id: 'results',
      label: 'Analytics',
      icon: Activity,
      path: `/tenant/flows/${flowId}/results`,
    },
  ];

  // Determina qual aba está ativa baseado na URL atual
  const getActiveTab = () => {
    const pathname = location.pathname;
    if (pathname.endsWith('/theme')) return 'theme';
    if (pathname.endsWith('/share')) return 'share';
    if (pathname.endsWith('/results')) return 'results';
    return 'flow';
  };

  const activeTab = getActiveTab();

  return (
    <div className="flex items-center gap-0.5 bg-muted/30 backdrop-blur-sm rounded-xl p-1 border border-border/50">
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;

        return (
          <button
            key={tab.id}
            onClick={() => navigate(tab.path)}
            className={cn(
              'relative flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200',
              isActive
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            )}
          >
            <Icon className="h-4 w-4" />
            <span>{tab.label}</span>
            {/* Indicador inferior de aba ativa */}
            {isActive && (
              <span className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-primary" />
            )}
          </button>
        );
      })}
    </div>
  );
}
